import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import WebApp from '@twa-dev/sdk';
import clsx from 'clsx';
import { t } from 'i18next';
import { matchPath } from 'react-router';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { useUser } from '@/entities/user/model/user.store';

import { ROUTE_PATHS } from '@/shared/config/router/types';
import {
  getCloudStorageValue,
  setCloudStorageValue,
} from '@/shared/helpers/cloud-storage';
import { useFeatureFlag } from '@/shared/hooks/use-feature-flag';
import { useMeasure } from '@/shared/hooks/use-measure';
import { Icon } from '@/shared/ui/icon';

import { BOINKER_VISITED_KEY } from '../constants';
import { createTabs } from '../helpers/createTabs';
import { getVisitedTabIndicatorKey } from '../helpers/getVisitedTabIndicatorKey';
import { updateNewGamesIndicatorIfNeeded } from '../helpers/updateNewGamesIndicator';
import { useIsActiveRoute } from '../hooks/useIsActiveRoute';
import {
  type LinkButton,
  type NavbarRoute,
  type NavbarRouteWithoutSlash,
} from '../types';

import s from './index.module.scss';

interface Props {
  className?: string;
  checkHasNavbar?: (value: boolean) => void;
}

export const Navbar = memo<Props>(({ className, checkHasNavbar }) => {
  const ref = useRef<HTMLDivElement>(null);

  const { isTestUser } = useFeatureFlag();

  const user = useUser();
  const { isNew } = user?.tests ?? {};

  const { pathname } = useLocation();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  const isActiveRoute = useIsActiveRoute(pathname);
  const [indicators, setIndicators] = useState<
    Partial<
      Record<`${NavbarRouteWithoutSlash}_visited` | typeof BOINKER_VISITED_KEY, true>
    >
  >({});

  const allParams = useMemo(() => {
    const res: Record<string, any> = {};
    searchParams.forEach((value, key) => (res[key] = value));
    return res;
  }, [searchParams]);

  const [urlParams, setUrlParams] = useState<Record<string, Record<string, any>>>({
    [pathname]: allParams,
  });

  useEffect(() => {
    setUrlParams((prev) => ({ ...prev, [pathname]: allParams }));
  }, [allParams, pathname]);

  const tabs = useMemo(() => {
    return createTabs({
      user,
      indicators,
      isNew: Boolean(isNew),
      urlParams,
      t,
    });
  }, [user, indicators, isNew, urlParams]);

  const hasNavbar = useMemo(
    () => tabs.some((tab) => isActiveRoute(tab.pathname)),
    [isActiveRoute, tabs]
  );

  useEffect(() => {
    checkHasNavbar?.(hasNavbar);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasNavbar]);

  useEffect(() => {
    void updateNewGamesIndicatorIfNeeded(pathname);
  }, [pathname]);

  // update visited tab indicators
  useEffect(() => {
    if (hasNavbar) {
      void (async () => {
        if (pathname === ROUTE_PATHS.SHOP) {
          await setCloudStorageValue(BOINKER_VISITED_KEY, 'true');
        }
        await setCloudStorageValue(
          getVisitedTabIndicatorKey(pathname.substring(1)),
          'true'
        );

        const indicators = Object.fromEntries(
          await Promise.all([
            // Map over tabs and return promises
            ...tabs.map((tab) => {
              const key = getVisitedTabIndicatorKey(tab.pathname.substring(1));
              return getCloudStorageValue(key).then((res) => {
                return [key, res === null || res === 'false' || res === ''] as const;
              });
            }),

            // Add 'boinker_visited'
            getCloudStorageValue(BOINKER_VISITED_KEY).then((res) => {
              return [
                BOINKER_VISITED_KEY,
                res === null || res === 'false' || res === '',
              ] as const;
            }),
          ])
        ) satisfies Partial<Record<NavbarRoute, boolean>>;

        setIndicators(indicators);
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const handleClick = useCallback(
    (link: LinkButton) => {
      WebApp.HapticFeedback.selectionChanged();

      if (matchPath(pathname, link.pathname)) {
        document
          .getElementById('root')
          ?.scrollTo({ left: 0, top: 0, behavior: 'smooth' });
        return;
      }

      let url: string = link.pathname;

      if (link.params) {
        url = `${url}?${link.params}`;
      }

      navigate(url, { state: { source: 'navbar' } });

      link?.cb?.();
    },
    [pathname, navigate]
  );

  const { clientHeight } = useMeasure(ref, [hasNavbar, WebApp.viewportStableHeight]);

  useEffect(() => {
    if (hasNavbar) {
      document.documentElement.style.setProperty('--navbar-height', `${clientHeight}px`);
    } else {
      document.documentElement.style.removeProperty('--navbar-height');
    }

    return () => {
      document.documentElement.style.removeProperty('--navbar-height');
    };
  }, [hasNavbar, clientHeight]);

  if (!hasNavbar) return null;

  return (
    <div ref={ref} className={clsx(s.container, className)}>
      <div className={s.content}>
        {tabs.map((tab) => {
          if (tab?.hidden && !isTestUser) return;

          return (
            // Use button instead of link to handle scroll to top behaviour
            // If use link there is conflict with scroll restoration
            <button
              ref={tab.ref}
              key={tab.pathname}
              {...(typeof tab.indicator === 'number' && tab.indicator
                ? { 'data-indicator': tab.indicator }
                : { 'data-indicator': '' })}
              className={clsx(
                s.tab,
                isActiveRoute(tab.pathname) && s.active,
                tab.indicator && s['has-indicator']
                // typeof tab.indicator === 'number' && s['indicator-counter']
              )}
              onClick={() => handleClick(tab)}
            >
              <Icon icon={tab.Icon} className={s.icon} />
              <span className={s.label}>{tab.title}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
});

Navbar.displayName = 'Navbar';
